// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSlice } from '@reduxjs/toolkit';
import deliveryEstimate from 'Utils/deliverables';
import { DeleteCustomBundleAction, GetAllCustomBundlesAction, GetBundlesFailureAction, GetBundlesStartAction, GetBundlesSuccessAction, ResetBundleStateAction, SelectedAdditionalDeliverablesAndLayersAction, SetAdditionalInstructionsAction, SetAllAttributeAddOnsSelectedAction, SetAllBundlesAction, SetAttributeAddOnsFactorAction, SetAttributeAddOnsListAction, SetCatalogBrowseModeAction, SetDeliverablesAction, SetDeliveryDateAction, SetDeliveryDaysAction, SetDensityAction, SetIncludeAttributeAction, SetNewCustomBundleAction, SetSurchargePolicyAction, SetTopoMajrAction, SetTopoMinrAction, SetUploadOwnDataAction, UpdateCustomBundleAction, UpdateSelectedAdditionalDeliverablesAndLayersAction } from './bundleActions';

const initialState: IBundleState = {
  list: [],
  availableBundles: [],
  additionalDeliverables: [],
  bundleDeliverables: [],
  selectedBundle: null,
  extraDeliverablesCost: 0,
  totalCost: 0,
  extraLayersCost: 0,
  selectedAdditionals: [],
  topoMajr: null,
  topoMinr: null,
  density: 'normal',
  densityOptions: [],
  deliverySpeed: 'normal',
  deliveryDays: 0,
  deliveryDate: null,
  loading: false,
  additionalInstructions: '',
  newCustomBundle: null,
  customBundles: [],
  allBundles: null,
  attributeAddOnsList: [],
  allAttributeAddOnsSelected: false,
  includeAttribute: false,
  attributeAddOnsFactor: 0,
  uploadOwnData: false,
  surchargePolicy: null,
  catalogBrowseMode: false,
};

const bundleSlice = createSlice({
  name: 'bundle',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GetBundlesStartAction, (state) => {
        state.loading = true;
      })
      .addCase(GetBundlesSuccessAction, (state, action) => {
        state.list = action.payload.deliverables.groupings;
        state.availableBundles = action.payload.deliverables.bundles;
        state.densityOptions = action.payload.deliverables.densityOptions;
        state.loading = false;
      })
      .addCase(GetAllCustomBundlesAction, (state, action) => {
        state.customBundles = action.payload;
      })
      .addCase(DeleteCustomBundleAction, (state, action) => {
        const customBundles = state.customBundles.filter((bundle) => bundle._id !== action.payload);
        state.customBundles = customBundles;
      })
      .addCase(UpdateCustomBundleAction, (state, action) => {
        const customBundles = state.customBundles.map((bundle) => {
          if (bundle._id === action.payload._id) {
            return { ...bundle, name: action.payload.name };
          }
          return bundle;
        });
        state.customBundles = customBundles;
      })
      .addCase(GetBundlesFailureAction, (state) => {
        state.loading = false;
      })
      .addCase(ResetBundleStateAction, () => initialState)
      .addCase(SetNewCustomBundleAction, (state, action) => {
        state.newCustomBundle = action.payload;
      })
      .addCase(SetDensityAction, (state, action) => {
        state.density = action.payload;
      })
      .addCase(SetDeliverablesAction, (state, action) => {
        state.additionalDeliverables = action.payload.additionalDeliverables;
        state.bundleDeliverables = action.payload.bundleDeliverables;
        state.selectedBundle = action.payload;
        state.extraLayersCost = 0;
        state.extraDeliverablesCost = 0;
        state.selectedAdditionals = [];
        state.totalCost = action.payload.bundleCost;
        state.density = action.payload.density;
        state.deliverySpeed = action.payload.deliverySpeed;
        state.deliveryDays = action.payload.bundleDeliveryTime;
        state.deliveryDate = deliveryEstimate(action.payload.bundleDeliveryTime);
        // Switching product resets the data-source choice back to the recommended provider.
        state.uploadOwnData = false;
      })
      .addCase(SelectedAdditionalDeliverablesAndLayersAction, (state, action) => {
        const index = state.selectedAdditionals.findIndex((s) => s._id === action.payload._id);
        if (index >= 0) {
          state.selectedAdditionals.splice(index, 1);
        } else {
          state.selectedAdditionals.push(action.payload);
        }
        state.totalCost = +(state.selectedBundle.bundleCost).toFixed(2);
      })
      .addCase(UpdateSelectedAdditionalDeliverablesAndLayersAction, (state, action) => {
        const index = state.selectedAdditionals.findIndex((s) => s._id === action.payload._id);
        if (index >= 0) {
          state.selectedAdditionals[index] = action.payload;
        }
      })
      .addCase(SetDeliveryDateAction, (state, action) => {
        state.deliveryDate = action.payload;
      })
      .addCase(SetDeliveryDaysAction, (state, action) => {
        state.deliveryDays = action.payload;
      })
      .addCase(SetTopoMajrAction, (state, action) => {
        state.topoMajr = action.payload;
      })
      .addCase(SetTopoMinrAction, (state, action) => {
        state.topoMinr = action.payload;
      })
      .addCase(SetAdditionalInstructionsAction, (state, action) => {
        state.additionalInstructions = action.payload;
      })
      .addCase(SetAllBundlesAction, (state, action) => {
        state.allBundles = action.payload;
      })
      .addCase(SetAttributeAddOnsListAction, (state, action) => {
        state.attributeAddOnsList = action.payload;
      })
      .addCase(SetAllAttributeAddOnsSelectedAction, (state, action) => {
        state.allAttributeAddOnsSelected = action.payload;
      })
      .addCase(SetIncludeAttributeAction, (state, action) => {
        state.includeAttribute = action.payload;
      })
      .addCase(SetAttributeAddOnsFactorAction, (state, action) => {
        state.attributeAddOnsFactor = action.payload;
      })
      .addCase(SetUploadOwnDataAction, (state, action) => {
        state.uploadOwnData = action.payload;
      })
      .addCase(SetSurchargePolicyAction, (state, action) => {
        state.surchargePolicy = action.payload;
      })
      .addCase(SetCatalogBrowseModeAction, (state, action) => {
        state.catalogBrowseMode = action.payload;
      });
  },
});

export const { reducer } = bundleSlice;

export default reducer;
